/* eslint-disable react/jsx-key */
import React, {memo, useEffect} from 'react';
import {
  HeaderGroup,
  Row,
  TableInstance,
  useColumnOrder,
  useFilters,
  useRowSelect,
  UseRowSelectInstanceProps,
  useSortBy,
  useTable,
} from 'react-table';
import classNames from 'classnames';
import {useDebounceCallback} from '~/hooks';
import Control from '~/components/elements/table/Control';
import {SELECTION_COLUMN} from '~/constants';
import {AppNoData, Row as UiRow, UiBox, UiCenter, UiIcon, UiInput, UiSpinner} from '..';
import {IndeterminateCheckbox} from '~/components/elements/table/RowSelectBox';
import {AiFillEdit, TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted} from 'react-icons/all';
import {Table, TableCellProps, Tbody, Td, Th, Thead, Tr} from '@chakra-ui/react';
import {isEmpty} from 'lodash';

interface Props {
  selectionVerticalAlign?: string;
  tableName: string;
  setFilters?: any;
  defaultFilters?: Record<string, any>;
  columns: any[];
  data: any[];
  loading?: boolean;
  onClickAdd?: any;
  onClickAddTitle?: string;
  onClickMassEdit?: any;
  onClickEdit?: any;
  onClickDeleteTitle?: string;
  onSelected?: any;
  onClickDelete?: (arg1: any[]) => any;
  module?: string;
  reloadPage?: boolean;
  children?: React.ReactNode;
  isHiddenCheckBox?: boolean;

  onInit?: (tableInstance: TableInstance) => void;
  onRowSelect?: any;
  onAllRowSelected?: any;
  tableMinH?: string;
}

const renderFirstColHeader =
  (isShowCheckBox: boolean, onAllRowSelected: any) =>
  ({getToggleAllRowsSelectedProps}: UseRowSelectInstanceProps<any>) => {
    return (
      <UiBox as="span" display="block">
        {isShowCheckBox && (
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} onAllRowSelected={onAllRowSelected} />
        )}
      </UiBox>
    );
  };

function renderFirstCellRow(isShowCheckBox: boolean, onClickEdit: any, onRowSelect: any) {
  const onClickEditLocal = (row: any) => {
    onClickEdit?.(row?.original);
  };

  return ({row}: {row: Row}) => {
    return (
      <UiRow as="span" display="block" alignItems="center">
        {isShowCheckBox && (
          <IndeterminateCheckbox row={row} {...row.getToggleRowSelectedProps()} onRowSelect={onRowSelect} />
        )}
        {typeof onClickEdit !== 'undefined' && (
          <UiIcon as={AiFillEdit} onClick={() => onClickEditLocal(row)} cursor="pointer" />
        )}
      </UiRow>
    );
  };
}

function renderFilterCpn(column: HeaderGroup<any>, onFieldChangeDebounce: any, defaultFilters?: Record<string, any>) {
  let Filter;
  if (column.canFilter && column.id !== SELECTION_COLUMN) {
    if (column.Filter) {
      Filter = column.render('Filter');
    } else {
      // render default input filter
      const queryParam = column.filterBy || column.id;
      const {placeholder, style, onClick, ...filterRest} = column.filterProps || {};
      Filter = (
        <UiInput
          variant="filled"
          minW="50px"
          placeholder={placeholder || '...'}
          onChange={({target}) => onFieldChangeDebounce({[queryParam]: target.value})}
          onClick={onClick ? onClick : e => e.stopPropagation()}
          defaultValue={defaultFilters?.[queryParam] || undefined}
          {...filterRest}
        />
      );
    }
  }
  return Filter;
}

export const AppTable = memo(
  ({
    selectionVerticalAlign,
    tableName,
    columns,
    data,
    loading,
    setFilters,
    defaultFilters,
    onClickAdd,
    onClickAddTitle,
    onClickDeleteTitle,
    onClickMassEdit,
    onClickEdit,
    onClickDelete,
    onSelected,
    reloadPage = true,
    module,
    children,
    isHiddenCheckBox,

    onInit,

    onRowSelect,
    onAllRowSelected,
    tableMinH = 'min-h-20',
    ...rest
  }: Props) => {
    const onFieldChangeDebounce = useDebounceCallback(setFilters, 300);
    const isShowCheckBox =
      typeof isHiddenCheckBox !== 'undefined'
        ? !isHiddenCheckBox
        : typeof onClickEdit !== 'undefined' ||
          typeof onClickDelete !== 'undefined' ||
          typeof onClickMassEdit !== 'undefined';

    const tableInstance = useTable(
      {
        columns,
        data,
        autoResetHiddenColumns: false,
        autoResetSortBy: false,
        manualSortBy: true,
      },
      useColumnOrder,
      useFilters,
      useSortBy,
      useRowSelect,
      hooks => {
        hooks.visibleColumns.push(visibleColumns => [
          {
            id: SELECTION_COLUMN,
            Header: renderFirstColHeader(isShowCheckBox, onAllRowSelected),
            Cell: renderFirstCellRow(isShowCheckBox, onClickEdit, onRowSelect),
            verticalAlign: selectionVerticalAlign || 'middle',
          },
          ...visibleColumns,
        ]);
      },
    );

    useEffect(() => {
      if (onInit) {
        onInit(tableInstance);
      }
    }, [onInit, tableInstance]);

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      setHiddenColumns,
      columns: tableColumns,
      selectedFlatRows,
    } = tableInstance;

    useEffect(() => {
      const hiddenCols: string[] = [];
      tableColumns.forEach(col => {
        if (col.isHidden === true) {
          hiddenCols.push(col.id);
        }
      });
      setHiddenColumns(hiddenCols);
    }, [setHiddenColumns, tableColumns]);

    if (typeof onSelected !== 'undefined') {
      onSelected(rows);
    }

    const renderTableBody = () => {
      if (isEmpty(data)) {
        return (
          <Tbody borderLeftWidth="1px" borderRightWidth="1px">
            <Tr>
              <Td colSpan={70}>
                <AppNoData />
              </Td>
            </Tr>
          </Tbody>
        );
      }
      return (
        <Tbody borderLeftWidth="1px" borderRightWidth="1px" {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} bg={row.isSelected ? 'easeMobile.100' : undefined}>
                {row.cells.map(cell => {
                  const {
                    className: cellClassName,
                    singleLine,
                    verticalAlign,
                    collapsing,
                    ...cellRest
                  } = cell.column.cellProps ||
                  ({} as TableCellProps & {singleLine?: boolean; collapsing?: boolean; verticalAlign?: string});

                  const className = classNames({'whitespace-nowrap': singleLine ?? true}, cellClassName);
                  let minWidth = '30px';
                  if (singleLine === false) {
                    minWidth = '210px';
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      minW={minWidth}
                      verticalAlign={verticalAlign || 'middle'}
                      className={className}
                      p={'8px'}
                      {...cellRest}
                    >
                      {/* @ts-ignore */}
                      {cell?.column?.Cell?.name === 'Cell' ? cell.render('Cell') : cell.render('Cell')}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      );
    };

    function renderTableHeader() {
      return (
        <Thead borderLeftWidth="1px" borderRightWidth="1px" borderTopWidth="1px">
          {headerGroups.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                const {...headerCellRest} = column.headerCellProps || {};

                const Filter = renderFilterCpn(column, onFieldChangeDebounce, defaultFilters);
                let SortIcon;
                if (column.canSort && column.id !== SELECTION_COLUMN) {
                  SortIcon = <UiIcon as={TiArrowUnsorted} boxSize={'14px'} />;
                  if (column.isSorted) {
                    SortIcon = column.isSortedDesc ? (
                      <UiIcon as={TiArrowSortedDown} boxSize={'14px'} />
                    ) : (
                      <UiIcon as={TiArrowSortedUp} boxSize={'14px'} />
                    );
                  }
                }
                const onClickHeaderCell = () => {
                  if (!column.canSort) {
                    return;
                  }
                  column.toggleSortBy(!column.isSortedDesc);
                  setFilters({orderBy: column.id, orderDirection: column.isSortedDesc ? 'asc' : 'desc'});
                };
                return (
                  <Th
                    className={classNames(
                      column.id === SELECTION_COLUMN ? 'align-bottom' : 'align-top',
                      'text-center whitespace-nowrap',
                      column.className,
                    )}
                    p={'8px'}
                    {...column.getHeaderProps()}
                    {...headerCellRest}
                  >
                    {column.render('Header')}
                    <UiBox as="span" _hover={{cursor: 'pointer'}} onClick={onClickHeaderCell}>
                      {SortIcon}
                    </UiBox>
                    <br />
                    {Filter}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
      );
    }

    return (
      <UiBox>
        <UiCenter
          position="absolute"
          bg={'white'}
          opacity={0.7}
          left={0}
          right={0}
          top={0}
          bottom={0}
          display={loading ? 'flex' : 'none'}
        >
          <UiSpinner size="xl" />
        </UiCenter>
        <Control
          tableName={tableName}
          tableState={tableInstance.state}
          {...tableInstance}
          onClickAdd={onClickAdd}
          onClickAddTitle={onClickAddTitle}
          onClickDeleteTitle={onClickDeleteTitle}
          onClickDelete={onClickDelete}
          allowCheckAll={isShowCheckBox}
          module={module}
          reloadPage={reloadPage}
        />
        <UiBox my={2}>
          <Table
            size="sm"
            variant="simple"
            className={tableMinH}
            display="block"
            overflowX="auto"
            sx={{
              '&::-webkit-scrollbar': {
                width: 0,
                height: 0,
              },
              '&::-webkit-scrollbar-thumb': {
                width: 0,
                height: 0,
              },
            }}
            {...rest}
            {...getTableProps()}
          >
            {renderTableHeader()}
            {renderTableBody()}
          </Table>
        </UiBox>
      </UiBox>
    );
  },
);
